'use client';
import { createContext, use, useMemo, type ReactNode } from 'react';
import { dirOf, type Dir, type Locale } from './locale';
import { formatMoney, currencyLabel } from './money';
import { createTranslate, type Messages, type Translate } from './translate';
import {
  formatDateLong,
  formatDateShort,
  formatDateTime,
  formatHour,
  formatTime,
  type IsoDate,
  type IsoInstant,
} from './time';

export interface I18n {
  locale: Locale;
  dir: Dir;
  t: Translate;
  /** "142.000 ر.ع", or the mask for null. */
  money: (baisa: number | null | undefined, opts?: { whole?: boolean }) => string;
  cur: string;
  dateLong: (date: IsoDate) => string;
  dateShort: (date: IsoDate) => string;
  hour: (hour: number, minute?: number) => string;
  time: (instant: IsoInstant | Date) => string;
  dateTime: (instant: IsoInstant | Date) => string;
}

const Ctx = createContext<I18n | null>(null);

export function makeI18n(locale: Locale, messages: Messages, warn = false): I18n {
  return {
    locale,
    dir: dirOf(locale),
    t: createTranslate(locale, messages, { warn }),
    money: (b, opts) => formatMoney(b, locale, opts),
    cur: currencyLabel(locale),
    dateLong: (d) => formatDateLong(locale, d),
    dateShort: (d) => formatDateShort(locale, d),
    hour: (h, m) => formatHour(locale, h, m),
    time: (i) => formatTime(locale, i),
    dateTime: (i) => formatDateTime(locale, i),
  };
}

export function I18nProvider(props: {
  locale: Locale;
  messages: Messages;
  warn?: boolean;
  children: ReactNode;
}) {
  const { locale, messages, warn, children } = props;
  const value = useMemo(() => makeI18n(locale, messages, warn), [locale, messages, warn]);
  return <Ctx value={value}>{children}</Ctx>;
}

export function useI18n(): I18n {
  const v = use(Ctx);
  if (!v) throw new Error('useI18n outside <I18nProvider>');
  return v;
}

export function useT(): Translate {
  return useI18n().t;
}
